import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@heroui-pro/react';
import type {
    AssetCategory,
    ChartAccount,
    FixedAsset,
    LocaleCode,
} from '@/api/models';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AssetFormView } from './AssetFormView';
import { useAssetForm } from './useAssetForm';

type AssetFormDrawerProps = {
    isOpen: boolean;
    /** The asset being edited; `null` = create. */
    asset: FixedAsset | null;
    categories: AssetCategory[];
    accounts: ChartAccount[];
    locale: LocaleCode;
    onClose: () => void;
    /** Fired once, after a real create success — never on edit, never on cancel. */
    onSaved?: () => void;
};

/**
 * Right-side drawer hosting the add / edit asset form — sibling of the Chart of
 * Accounts CRUD drawer and the journal entry drawer (same HeroUI Pro `Sheet`
 * chrome, vocabulary and focus handling). The register stays visible behind it.
 * The Sheet owns focus trap and restore; the hook owns all behaviour.
 *
 * Creating hands the resulting **draft** acquisition entry to the shared Journal
 * Entry form, exactly as the VAT settlement does — its validation, period gating
 * and Post button apply unchanged, and nothing posts without the user saying so.
 */
export const AssetFormDrawer: React.FC<AssetFormDrawerProps> = ({
    isOpen,
    asset,
    categories,
    accounts,
    locale,
    onClose,
    onSaved,
}) => {
    const { t } = useTranslation('pages');
    const form = useAssetForm({
        asset,
        categories,
        accounts,
        locale,
        onClose,
        onSaved,
    });
    const isEdit = asset !== null;

    return (
        <>
            <Sheet
                isOpen={isOpen}
                onOpenChange={(open) => !open && onClose()}
                placement="right"
            >
                <Sheet.Backdrop>
                    <Sheet.Content className="w-full sm:max-w-[560px]">
                        <Sheet.Dialog
                            aria-label={t(
                                isEdit
                                    ? 'fixedAssets.form.editTitle'
                                    : 'fixedAssets.form.createTitle',
                            )}
                            {...getNodeTestIdProp('fixed-assets-form-drawer')}
                        >
                            <Sheet.CloseTrigger />
                            <Sheet.Header>
                                <Sheet.Heading>
                                    <span className="flex flex-col gap-1">
                                        <span className="text-lg font-semibold">
                                            {t(
                                                isEdit
                                                    ? 'fixedAssets.form.editTitle'
                                                    : 'fixedAssets.form.createTitle',
                                            )}
                                        </span>
                                        {/*
                                          The subtitle follows the choice: an
                                          acquisition prepares an entry, an
                                          opening asset prepares nothing. Leaving
                                          the acquisition wording up for both
                                          would promise a draft that never comes.
                                        */}
                                        <span className="text-sm font-normal text-muted">
                                            {t(
                                                isEdit
                                                    ? 'fixedAssets.form.editSubtitle'
                                                    : form.isOpening
                                                      ? 'fixedAssets.form.createSubtitleOpening'
                                                      : 'fixedAssets.form.createSubtitle',
                                            )}
                                        </span>
                                    </span>
                                </Sheet.Heading>
                            </Sheet.Header>

                            <Sheet.Body>
                                <AssetFormView form={form} onCancel={onClose} />
                            </Sheet.Body>
                        </Sheet.Dialog>
                    </Sheet.Content>
                </Sheet.Backdrop>
            </Sheet>

            <JournalEntryModal
                entry={form.draft}
                isOpen={form.draft !== null}
                onClose={form.onDraftClose}
                onSaved={form.onDraftSaved}
            />
        </>
    );
};
